import {useEffect, useRef} from 'react';
import {useAtom, useSetAtom} from 'jotai';
import {logger} from '@/lib/logger';

const log = logger.tag('BackgroundPoller');
import {
    processingNotificationsAtom,
    updateNotificationAtom,
    type Notification,
} from '@/store/notifications';
import {getVideoResults} from '@/actions/ai/video-generation';
import {searchIO} from '@/actions/products/media';

/**
 * Global background poller that runs everywhere in the app
 * Polls for completion of video, silo, and 360-spin generation
 */
export function useBackgroundPoller() {
    const [processingNotifications] = useAtom(processingNotificationsAtom);
    const updateNotification = useSetAtom(updateNotificationAtom);
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
        null
    );
    const processingNotificationsRef = useRef(processingNotifications);

    // Keep refs up to date
    useEffect(() => {
        processingNotificationsRef.current = processingNotifications;
    }, [processingNotifications]);

    useEffect(() => {
        // Check if we have anything to poll
        const hasProcessingItems = processingNotifications.length > 0;

        // No processing items, stop polling
        if (!hasProcessingItems) {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            return;
        }

        // Start polling if not already polling
        pollingIntervalRef.current ??= setInterval(async () => {
            const currentNotifications = processingNotificationsRef.current;

            // Poll notifications
            for (const notification of currentNotifications) {
                try {
                    // Skip cancelled notifications
                    if (notification.status === 'cancelled') {
                        continue;
                    }

                    // Handle different notification types
                    switch (notification.type) {
                        case 'video':
                            await pollVideoGeneration(
                                notification,
                                updateNotification
                            );
                            break;
                        case 'silo':
                        case '360-spin':
                            await pollRenderGeneration(
                                notification,
                                updateNotification
                            );
                            break;
                    }
                } catch (err) {
                    log.error('Error polling notification', err, {
                        notificationId: notification.id,
                    });
                }
            }
        }, 20000); // Poll every 20 seconds

        // Cleanup on unmount
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [processingNotifications.length, updateNotification]);
}

type UpdateNotificationFn = (update: {
    id: string;
    changes: Partial<Notification>;
}) => void;

/**
 * Video generation result structure
 */
interface VideoResult {
    finished: boolean;
    output?: string[];
}

/**
 * Render item structure from searchIO response
 */
interface RenderItem {
    id: string | number;
    state: string;
    url?: string;
    thumbnail_url?: string;
    created_at?: string;
    createdAt?: Date;
}

/**
 * Poll for video generation completion
 */
async function pollVideoGeneration(
    notification: Notification,
    updateNotification: UpdateNotificationFn
) {
    if (!notification.requestId) {
        return;
    }

    try {
        const result = (await getVideoResults(
            notification.requestId
        )) as VideoResult;

        if (result.finished) {
            const completedAt = new Date().toISOString();

            if (result.output && result.output.length > 0) {
                // Auto-save video to media library with visible: false
                try {
                    // Dynamically import the save function
                    const {saveAILifestyleImageToGroupMedia} =
                        await import('@/actions/groups/media');

                    if (
                        notification.assetType === 'group' &&
                        notification.productId
                    ) {
                        await saveAILifestyleImageToGroupMedia(
                            notification.productId.toString(),
                            result.output[0],
                            notification.message || 'AI Generated Video',
                            {visible: false, format: 'video'}
                        );
                        // console.log(
                        //     '[Video Polling] Video auto-saved to media library (not visible)'
                        // );
                    }
                } catch (saveError) {
                    log.error('Failed to auto-save video', saveError);
                }

                // Update notification to completed
                updateNotification({
                    id: notification.id,
                    changes: {
                        status: 'completed' as const,
                        resultUrl: result.output[0],
                        completedAt,
                    },
                });
            } else {
                log.error('Video generation failed - no output', null, {
                    result,
                });

                // Update notification to failed
                updateNotification({
                    id: notification.id,
                    changes: {
                        status: 'failed' as const,
                        message: 'Video generation failed. Please try again.',
                        completedAt,
                    },
                });
            }
        } else {
            // console.log('[Video Polling] ⏳ Still processing...');
        }
    } catch (err) {
        log.error('Error checking video status', err);
    }
}

/**
 * Poll for silo/360-spin render generation completion
 */
async function pollRenderGeneration(
    notification: Notification,
    updateNotification: UpdateNotificationFn
) {
    if (!notification.productId) {
        log.error('No productId found for notification', null, {
            notificationId: notification.id,
        });
        return;
    }

    try {
        const typeName = notification.type === 'silo' ? 'Silo' : '360-Spin';
        // console.log(
        //     `[${typeName} Polling] Checking renders for product:`,
        //     notification.productId
        // );

        // Search for renders created after the notification
        const response = await searchIO({
            asset_id: notification.productId,
            per_page: 10,
        });

        if (response.items && response.items.length > 0) {
            // Check if there are any renders created after this notification was created
            const notificationTime = new Date(notification.createdAt).getTime();
            // console.log(
            //     `[${typeName} Polling] Found ${response.items.length} total renders`
            // );
            // console.log(
            //     `[${typeName} Polling] Notification created at:`,
            //     new Date(notification.createdAt).toISOString()
            // );
            // console.log(
            //     `[${typeName} Polling] Render states:`,
            //     response.items
            //         .map((item: RenderItem) => `${item.id}:${item.state}`)
            //         .join(', ')
            // );

            const newRenders = response.items.filter((item: RenderItem) => {
                // Handle both created_at and createdAt fields
                const itemCreatedAt = item.created_at ?? item.createdAt;
                if (!itemCreatedAt) {
                    console.warn(
                        `[${typeName} Polling] Render missing created_at field:`,
                        item.id
                    );
                    return false;
                }
                const itemTime = new Date(itemCreatedAt).getTime();
                const isAfterNotification = itemTime >= notificationTime;
                const isComplete = item.state === 'success';
                // console.log(
                //     `[${typeName} Polling] Checking render ${item.id}: created=${new Date(itemCreatedAt).toISOString()}, after=${isAfterNotification}, complete=${isComplete}`
                // );
                return isAfterNotification && isComplete;
            });

            if (newRenders.length > 0) {
                // console.log(
                //     `[${typeName} Polling] ✓ Completed renders found:`,
                //     newRenders.length
                // );

                // Get the first render URL
                const firstRender = newRenders[0];
                const resultUrl = firstRender.url ?? firstRender.thumbnail_url;
                const completedAt = new Date().toISOString();

                // Update notification to completed
                updateNotification({
                    id: notification.id,
                    changes: {
                        status: 'completed' as const,
                        resultUrl: resultUrl,
                        message: `${typeName} renders completed`,
                        completedAt,
                    },
                });
            } else {
                // console.log(`[${typeName} Polling] ⏳ Still processing...`);
            }
        }
    } catch (err) {
        log.error('Error checking render status', err);
    }
}
